import { FC, useMemo, useCallback } from 'react'
import * as invert from 'lodash/invert'
import { AnyDict } from 'interfaces'
import { ProviderAuthenticationFormValues } from 'interfaces/form'
import { getMangledFieldNames } from 'helpers/formHelpers'
import { LinkageForm as LinkageFormType, WithLinkageSessionId } from 'interfaces/linkage'
import { authenticateLinkageActionCreator } from 'actions/linkageActions'
import LinkageForm from 'components/molecules/forms/LinkageForm'
import { useCidBySessionId } from 'helpers/linkageHelpers'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { getItemFromCollection } from 'helpers/normalizeHelpers'

type LinkageAuthenticationProps =
  & WithLinkageSessionId

export const LinkageAuthentication: FC<LinkageAuthenticationProps> = (props) => {
  const {
    sessionId,
  } = props

  const { withCid, session } = useCidBySessionId(sessionId)
  const form: LinkageFormType | null = useStoreSelector((state) => getItemFromCollection(state.data.linkage.linkageForms, withCid.cid))

  const memo = useMemo(() => {
    if (!form) {
      return null
    }

    const { fields } = form

    const mangledFieldNames = getMangledFieldNames(fields)

    const values = fields.reduce<ProviderAuthenticationFormValues>(
      (result, field) => {
        if (field.type === 'option' || field.type === 'radio') {
          const { options, name } = field
          return {
            ...result,
            [mangledFieldNames[name]]: (options[0] || { value: undefined }).value,
          }
        }

        return result
      },
      {}
    )

    const mangledFields = fields.map((field) => ({ ...field, name: mangledFieldNames[field.name] }))

    return {
      initialValues: values,
      mangledFields,
    }
  }, [form])

  const dispatch = useStoreDispatch()
  const handleFormSubmit = useCallback<(values: AnyDict) => {}>((values) => {
    if (!session || !form) {
      return {}
    }

    const mangledFieldNames = getMangledFieldNames(form.fields)
    const unmangledFieldNames = invert(mangledFieldNames) as Record<string, string>
    const unmangledValues = Object.keys(values).reduce(
      (result, key) => ({ ...result, [unmangledFieldNames[key]]: values[key] }),
      {}
    )

    dispatch(authenticateLinkageActionCreator.request({ session, formValues: unmangledValues }))

    return {}
  }, [dispatch, form, session])

  if (!memo) {
    return null
  }

  return (
    <LinkageForm
      sessionId={ sessionId }
      fields={ memo.mangledFields }
      initialValues={ memo.initialValues }
      onSubmit={ handleFormSubmit }
    />
  )
}
